/**
 * Transform raw API responses into Today-screen domain types.
 *
 * Each transformer is defensive: it handles missing / malformed fields
 * gracefully rather than throwing. If a backend shape is unknown, we
 * return an empty array — the UI then shows the empty/fallback state.
 */

import type {
  AttentionProject,
  ScoredDeal,
  Approval,
  Decision,
  DueFollowUp,
  Priority,
  ProjectsResponse,
  DealsResponse,
  ApprovalsResponse,
  ExecutiveBriefingResponse,
  ExecutiveDecisionsResponse,
  WatchlistDueResponse,
  ProjectStatus,
  ScoreTier,
  PriorityLevel,
} from "./types.js";

function toProjectStatus(s?: string): ProjectStatus {
  if (s === "red" || s === "yellow" || s === "green") return s;
  if (s === "at_risk" || s === "blocked") return "red";
  if (s === "warning") return "yellow";
  return "green";
}

function toScoreTier(score: number): ScoreTier {
  if (score >= 75) return "high";
  if (score >= 50) return "medium";
  return "low";
}

function toPriorityLevel(s?: string): PriorityLevel {
  if (s === "urgent" || s === "high" || s === "medium" || s === "low") return s;
  return "medium";
}

export function transformProjects(data: unknown): AttentionProject[] {
  try {
    const d = data as ProjectsResponse;
    if (!Array.isArray(d?.projects)) return [];

    return d.projects
      .filter((p) => p.needsAttention || p.status === "red" || p.status === "yellow")
      .slice(0, 5)
      .map((p) => ({
        id: String(p.id),
        name: p.name ?? "Unnamed Project",
        status: toProjectStatus(p.status),
        reason: p.healthReason ?? "Needs review",
        phase: p.phase,
      }));
  } catch {
    return [];
  }
}

/**
 * Transform GET /agents/development/delays into an attention list.
 *
 * The plain GET /projects response returns `health: null` for every project,
 * so `transformProjects` can never surface a delayed project — it always shows
 * "all on track." The delays agent, by contrast, computes per-project overdue /
 * at-risk milestones. We map those into the same AttentionProject shape so the
 * "Projects Needing Attention" card reflects reality (matches ProjectsPage #50).
 *
 * A project is "red" if it has any overdue milestone, "yellow" if only at-risk.
 * Projects with neither are on track and excluded.
 */
export function transformDelaysToAttention(data: unknown): AttentionProject[] {
  try {
    const d = data as { delays?: unknown };
    if (!Array.isArray(d?.delays)) return [];

    return d.delays
      .map((raw) => {
        const e = raw as {
          projectId?: unknown;
          projectName?: unknown;
          overdueMilestones?: unknown;
          atRiskMilestones?: unknown;
          healthColor?: unknown;
        };
        const overdue = Array.isArray(e.overdueMilestones) ? e.overdueMilestones.length : 0;
        const atRisk = Array.isArray(e.atRiskMilestones) ? e.atRiskMilestones.length : 0;
        return { e, overdue, atRisk };
      })
      .filter(({ overdue, atRisk, e }) => overdue > 0 || atRisk > 0 || e.healthColor === "red" || e.healthColor === "yellow")
      .map(({ e, overdue, atRisk }) => {
        const status: ProjectStatus =
          e.healthColor === "red" || e.healthColor === "yellow"
            ? toProjectStatus(String(e.healthColor))
            : overdue > 0
              ? "red"
              : "yellow";
        const parts: string[] = [];
        if (overdue > 0) parts.push(`${overdue} overdue milestone${overdue === 1 ? "" : "s"}`);
        if (atRisk > 0) parts.push(`${atRisk} at-risk`);
        return {
          id: e.projectId != null ? String(e.projectId) : "",
          name: e.projectName != null ? String(e.projectName) : "Unnamed Project",
          status,
          reason: parts.join(", ") || "Needs review",
          phase: undefined,
        };
      })
      // Show the most urgent first: red before yellow.
      .sort((a, b) => (a.status === "red" ? 0 : 1) - (b.status === "red" ? 0 : 1))
      .slice(0, 5);
  } catch {
    return [];
  }
}

/**
 * Transform deals data from GET /deals/scored.
 *
 * A2 bug fix: the live backend returns `d.properties` (not `d.deals`).
 * We now accept both keys — `properties` takes precedence (live API shape),
 * with `deals` as a legacy fallback. Property rows are mapped via the same
 * field adapter as DealsPage's propertyToDealCard.
 */
export function transformDeals(data: unknown): ScoredDeal[] {
  try {
    const d = data as DealsResponse;

    // Live API shape: { properties: ScoredPropertyRaw[] }
    if (Array.isArray(d?.properties)) {
      return d.properties
        .slice(0, 5)
        .map((p) => {
          const score = Number(p.score ?? 0);
          // Format cap rate from decimal (0.065) → "6.5%" if present
          const capRateStr =
            p.cap_rate !== undefined
              ? `${(p.cap_rate * 100).toFixed(1)}%`
              : undefined;
          // Format price from number to readable string
          const askPriceStr =
            p.price !== undefined
              ? p.price >= 1_000_000
                ? `$${(p.price / 1_000_000).toFixed(2)}M`
                : p.price >= 1_000
                  ? `$${(p.price / 1_000).toFixed(0)}K`
                  : `$${p.price.toLocaleString()}`
              : undefined;

          const deal: ScoredDeal = {
            id: p.entity_id,
            address:
              p.address ?? p.entity_id.replace(/^property:/, "") ?? "Unknown address",
            score,
            scoreTier: toScoreTier(score),
          };
          if (askPriceStr !== undefined) deal.askPrice = askPriceStr;
          if (capRateStr !== undefined) deal.capRate = capRateStr;
          if (p.last_activity !== undefined) {
            deal.addedDate = new Date(p.last_activity).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
          }
          return deal;
        })
        .sort((a, b) => b.score - a.score);
    }

    // Legacy / pre-mapped shape: { deals: [...] }
    if (Array.isArray(d?.deals)) {
      return d.deals
        .slice(0, 5)
        .map((deal) => {
          const score = Number(deal.score ?? 0);
          const result: ScoredDeal = {
            id: String(deal.id),
            address: deal.address ?? deal.title ?? "Unknown address",
            score,
            scoreTier: toScoreTier(score),
          };
          if (deal.askPrice !== undefined) result.askPrice = deal.askPrice;
          if (deal.capRate !== undefined) result.capRate = deal.capRate;
          if (deal.createdAt !== undefined) {
            result.addedDate = new Date(deal.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            });
          }
          return result;
        })
        .sort((a, b) => b.score - a.score);
    }

    return [];
  } catch {
    return [];
  }
}

export function transformApprovals(data: unknown): Approval[] {
  try {
    const d = data as ApprovalsResponse;
    if (!Array.isArray(d?.approvals)) return [];

    return d.approvals.slice(0, 6).map((a) => ({
      id: String(a.id),
      title: a.title ?? "Untitled action",
      project: a.projectName ?? a.project ?? "Unknown project",
      type: a.type ?? "Decision",
      requestedBy: a.requestedBy,
      requestedAt: a.requestedAt,
    }));
  } catch {
    return [];
  }
}

/**
 * Transform the executive briefing's priorities array into Priority items.
 */
export function transformBriefingPriorities(data: unknown): Priority[] {
  try {
    const d = data as ExecutiveBriefingResponse;
    if (!Array.isArray(d?.priorities)) return [];

    return d.priorities.slice(0, 5).map((p, i) => {
      const item: Priority = {
        id: p.id ?? `briefing-priority-${i}`,
        title: p.title,
        level: toPriorityLevel(p.level),
      };
      if (p.project !== undefined) item.project = p.project;
      if (p.dueDate !== undefined) item.dueDate = p.dueDate;
      if (p.type !== undefined) item.type = p.type;
      return item;
    });
  } catch {
    return [];
  }
}

/**
 * Transform the executive briefing's projectsNeedingAttention array.
 */
export function transformBriefingProjects(data: unknown): AttentionProject[] {
  try {
    const d = data as ExecutiveBriefingResponse;
    if (!Array.isArray(d?.projectsNeedingAttention)) return [];

    return d.projectsNeedingAttention.slice(0, 5).map((p, i) => ({
      id: p.id ?? `briefing-project-${i}`,
      name: p.name,
      status: toProjectStatus(p.status),
      reason: p.reason ?? "Needs attention",
      phase: p.phase,
    }));
  } catch {
    return [];
  }
}

/**
 * Transform the executive briefing's newOpportunities into ScoredDeal items.
 */
export function transformBriefingOpportunities(data: unknown): ScoredDeal[] {
  try {
    const d = data as ExecutiveBriefingResponse;
    if (!Array.isArray(d?.newOpportunities)) return [];

    return d.newOpportunities.slice(0, 5).map((o, i) => {
      const score = Number(o.score ?? 0);
      const deal: ScoredDeal = {
        id: o.id ?? `briefing-opp-${i}`,
        address: o.address,
        score,
        scoreTier: toScoreTier(score),
      };
      if (o.askPrice !== undefined) deal.askPrice = o.askPrice;
      if (o.capRate !== undefined) deal.capRate = o.capRate;
      return deal;
    });
  } catch {
    return [];
  }
}

/**
 * Transform the executive decisions queue into Decision items.
 */
export function transformDecisions(data: unknown): Decision[] {
  try {
    const d = data as ExecutiveDecisionsResponse;
    if (!Array.isArray(d?.decisions)) return [];

    return d.decisions.slice(0, 6).map((item, i) => {
      const decision: Decision = {
        id: item.id ?? `decision-${i}`,
        title: item.title ?? "Untitled decision",
      };
      if (item.project !== undefined) decision.project = item.project;
      if (item.type !== undefined) decision.type = item.type;
      if (item.context !== undefined) decision.context = item.context;
      if (item.urgency !== undefined) decision.urgency = item.urgency;
      if (item.requestedAt !== undefined) decision.requestedAt = item.requestedAt;
      return decision;
    });
  } catch {
    return [];
  }
}

/**
 * Transform the watchlist/due response into DueFollowUp items.
 */
export function transformDueFollowUps(data: unknown): DueFollowUp[] {
  try {
    const d = data as WatchlistDueResponse;
    if (!Array.isArray(d?.due)) return [];

    return d.due.slice(0, 5).map((item, i) => {
      const followUp: DueFollowUp = {
        id: item.id ?? `due-${i}`,
        title: item.title ?? "Follow-up",
      };
      if (item.dueDate !== undefined) followUp.dueDate = item.dueDate;
      if (item.contact !== undefined) followUp.contact = item.contact;
      if (item.property !== undefined) followUp.property = item.property;
      return followUp;
    });
  } catch {
    return [];
  }
}
